import { pickedFile } from '../../../utils/pickedFile.js'
import MediaCaptureField from '../../common/MediaCaptureField.js'
import React, { useState } from 'react';
import Alert from '../../common/Alert';
import SelectField from '../../common/SelectField.js';
import companySubscriptionService from '../../../services/CompanySubscriptionService';
import { X, Camera} from 'lucide-react';
import { validators, inputFilters, useValidation, ValidationMessage } from '../../common/Validation.js';
import Button from '../../common/Button.js';
import { useTranslation } from 'react-i18next';


/**
 * Path: src/components/company-subscription/subscription_payment/AddPaymentModal.js
 *
 * Props:
 *   subscription  – full subscription object (from to_dict)
 *   totalPaid     – total completed payments so far (number)
 *   onClose       – fn
 *   onSuccess     – fn
 */
const AddPaymentModal = ({ subscription, totalPaid, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    amount:               '',
    payment_method:       'cash',
    payment_status:       'pending',
    payment_date:         new Date().toISOString().split('T')[0],
    transaction_id:       '',
    transaction_reference: '',
    bank_name:            '',
    cheque_number:        '',
    upi_id:               '',
    notes:                '',
    remarks:              '',
  });
  const [receiptFile, setReceiptFile]   = useState(null);
  const [filePreview, setFilePreview]   = useState(null);
  const [loading, setLoading]           = useState(false);

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    amount:       (v) => validators.required(v, 'Amount') || validators.number(v, { min: 0.01, label: 'Amount' }),
    payment_date: (v) => validators.required(v, 'Payment date'),
  };

  const paymentMethods = [
    { value: 'cash',          label: 'Cash' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cheque',        label: 'Cheque' },
    { value: 'upi',           label: 'UPI' },
    { value: 'credit_card',   label: 'Credit Card' },
    { value: 'debit_card',    label: 'Debit Card' },
    { value: 'net_banking',   label: 'Net Banking' },
    { value: 'other',         label: 'Other' },
  ];

  const paymentStatuses = [
    { value: 'pending',   label: 'Pending' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed',    label: 'Failed' },
  ];

  // Max = subscription_amount - completed_paid
  const subscriptionAmount = parseFloat(subscription?.subscription_amount || 0);
  const pendingBalance = Math.max(0, subscriptionAmount - parseFloat(totalPaid || 0));

  const showExtraFields = {
    bank_transfer: ['bank_name', 'transaction_id', 'transaction_reference'],
    cheque:        ['bank_name', 'cheque_number'],
    upi:           ['upi_id', 'transaction_id'],
    credit_card:   ['transaction_id', 'transaction_reference'],
    debit_card:    ['transaction_id', 'transaction_reference'],
    net_banking:   ['bank_name', 'transaction_id'],
    online:        ['transaction_id', 'transaction_reference'],
  };

  const extraFields = showExtraFields[formData.payment_method] || [];

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'amount') filtered = inputFilters.amount(value);

    setFormData(prev => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleFileChange = (e) => {
    const file = pickedFile(e);
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { Alert.error('File must be under 5 MB'); return; }
    setReceiptFile(file);
    if (file.type.startsWith('image/')) {
      const r = new FileReader();
      r.onloadend = () => setFilePreview(r.result);
      r.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const removeFile = () => {
    setReceiptFile(null);
    setFilePreview(null);
    const el = document.getElementById('add-sub-pay-file');
    if (el) el.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    const entered = parseFloat(formData.amount);
    if (entered > pendingBalance) {
      Alert.error(`Amount exceeds pending balance: ₹${pendingBalance.toLocaleString('en-IN')}`); return;
    }

    setLoading(true);
    try {
      const paymentData = {
        subscription_id:       subscription.id,
        amount:                formData.amount,
        payment_method:        formData.payment_method,
        payment_status:        formData.payment_status,
        payment_date:          formData.payment_date,
        transaction_id:        formData.transaction_id       || undefined,
        transaction_reference: formData.transaction_reference || undefined,
        bank_name:             formData.bank_name            || undefined,
        cheque_number:         formData.cheque_number        || undefined,
        upi_id:                formData.upi_id               || undefined,
        notes:                 formData.notes                || undefined,
        remarks:               formData.remarks              || undefined,
      };

      // Remove undefined keys
      Object.keys(paymentData).forEach(k => paymentData[k] === undefined && delete paymentData[k]);

      await companySubscriptionService.createPayment(paymentData, receiptFile);
      Alert.success('Payment recorded successfully!');
      onSuccess();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm";
  const labelCls = "block text-sm font-medium text-secondary mb-1.5";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-secondary">Record Payment</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">

          {/* Subscription summary */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-500">Subscription ID</p>
                <p className="font-semibold text-secondary">{subscription?.subscription_id}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Amount</p>
                <p className="font-bold text-orange-600">₹{subscriptionAmount.toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Already Paid</p>
                <p className="font-bold text-green-600">₹{parseFloat(totalPaid || 0).toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Pending Balance</p>
                <p className={`font-bold ${pendingBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ₹{pendingBalance.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </div>

          {/* Amount + Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Amount <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter amount"
                className={`${inputCls} ${errors.amount ? 'border-red-400' : ''}`}
                required
              />
              <ValidationMessage error={errors.amount} />
              <p className="text-xs text-gray-500 mt-1">
                Max: <span className="font-semibold text-orange-600">₹{pendingBalance.toLocaleString('en-IN')}</span>
              </p>
            </div>
            <div>
              <label className={labelCls}>Payment Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                name="payment_date"
                value={formData.payment_date}
                onChange={handleChange}
                className={`${inputCls} ${errors.payment_date ? 'border-red-400' : ''}`}
                required
              />
              <ValidationMessage error={errors.payment_date} />
            </div>
          </div>

          {/* Method + Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Payment Method <span className="text-red-500">*</span></label>
              <SelectField variant="form" name="payment_method" value={formData.payment_method} onChange={handleChange} className={inputCls}>
                {paymentMethods.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </SelectField>
            </div>
            <div>
              <label className={labelCls}>Payment Status <span className="text-red-500">*</span></label>
              <SelectField variant="form" name="payment_status" value={formData.payment_status} onChange={handleChange} className={inputCls}>
                {paymentStatuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </SelectField>
            </div>
          </div>

          {/* Conditional fields based on method */}
          {extraFields.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {extraFields.includes('transaction_id') && (
                <div>
                  <label className={labelCls}>Transaction ID</label>
                  <input type="text" name="transaction_id" value={formData.transaction_id}
                    onChange={handleChange} placeholder="Transaction ID" className={inputCls} />
                </div>
              )}
              {extraFields.includes('transaction_reference') && (
                <div>
                  <label className={labelCls}>Transaction Reference</label>
                  <input type="text" name="transaction_reference" value={formData.transaction_reference}
                    onChange={handleChange} placeholder="UTR / Reference no." className={inputCls} />
                </div>
              )}
              {extraFields.includes('bank_name') && (
                <div>
                  <label className={labelCls}>Bank Name</label>
                  <input type="text" name="bank_name" value={formData.bank_name}
                    onChange={handleChange} placeholder="Bank name" className={inputCls} />
                </div>
              )}
              {extraFields.includes('cheque_number') && (
                <div>
                  <label className={labelCls}>Cheque Number</label>
                  <input type="text" name="cheque_number" value={formData.cheque_number}
                    onChange={handleChange} placeholder="Cheque number" className={inputCls} />
                </div>
              )}
              {extraFields.includes('upi_id') && (
                <div>
                  <label className={labelCls}>UPI ID</label>
                  <input type="text" name="upi_id" value={formData.upi_id}
                    onChange={handleChange} placeholder="UPI ID / VPA" className={inputCls} />
                </div>
              )}
            </div>
          )}

          {/* Notes + Remarks */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange}
                rows="2" placeholder="Internal notes..." className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Remarks</label>
              <textarea name="remarks" value={formData.remarks} onChange={handleChange}
                rows="2" placeholder="Optional remarks..." className={inputCls} />
            </div>
          </div>

          {/* Receipt */}
<div>
  <label className={labelCls}>Payment Receipt (Optional)</label>

  <div className="mt-2">
    <MediaCaptureField mode="document" onPick={handleFileChange} />

    <label
      htmlFor="add-sub-pay-file"
      className="w-40 h-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center relative bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
    >
      {!filePreview ? (
        <div className="text-center">
          <Camera className="mx-auto w-8 h-8 text-gray-400 mb-2" />
          <p className="text-xs text-gray-500">Click to upload</p>
        </div>
      ) : (
        <img
          src={filePreview}
          alt="Preview"
          className="w-full h-full object-contain rounded-lg"
        />
      )}

      {receiptFile && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            removeFile();
          }}
          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
        >
          ✕
        </button>
      )}
    </label>

    <p className="text-xs text-gray-500 mt-2">
      Images or PDF, max 5MB
    </p>
  </div>
</div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button variant="primary" type="submit" loading={loading} className="flex-1">
              {loading ? 'Recording...' : 'Record Payment'}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPaymentModal;